import React, { useEffect, useContext, useState, useRef } from 'react';
import { Modal, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import SvgGrid from './SvgGrid';
import SymbolAvgVoltage from './SymbolAvgVoltage';
import SymbolAvgHz from './SymbolAvgHz';
import "./Svg.css";
import BatteryAlarmDefs from './BatteryAlarmDefs';
import InverterAlarmDefs from './InverterAlarmDefs';
import utils from '../../../utils';
import SvgButton from "./SvgButton";
import ForceSOCCalibration from "./ForceSOCCalibration";
import MeterDirectionDetection from "./MeterDirectionDetection";


const moment = require('moment-timezone');

function SvgRight({ SN, x, y, width, height, master, Combinations, onRefresh }) {
    const { t, i18n } = useTranslation();
    const setTip = useContext(utils.tipContext);

    const [sns, setSns] = useState([])
    const refForceSOCCalibration = useRef();
    const refMeterDirectionDetection = useRef();

    let faults = [];

    useEffect(() => {
        if (!Combinations) {
            setSns([])
            return;
        }

        let sns = Combinations.map(v => v.SN);
        setSns(sns)

    }, [Combinations])

    function convertTimeZone(timeString, fromTimeZone, toTimeZone) {
        const date = moment.tz(timeString, 'YYYY-MM-DD HH:mm:ss', fromTimeZone);

        const convertedDate = date.tz(toTimeZone);

        return convertedDate.format('YYYY-MM-DD HH:mm:ss');
    }

    function getBrowserTimezone() {
        const formatter = new Intl.DateTimeFormat();
        const timeZone = formatter.resolvedOptions().timeZone;
        return timeZone;
    }

    const handleClick = () => {
        Modal.info({
            maskClosable: true,
            width: "70%",
            icon: null,
            title: t("Grid Data"),
            content: (<Table
                pagination={false}
                columns={[
                    {
                        title: t("Inverter SN"), dataIndex: "SN", align: "right", render: (v, r) => {
                            return `${v}${r.Phase == 2 ? "(A)" : r.Phase == 3 ? "(B)" : r.Phase == 4 ? "(C)" : ""}`;
                        }
                    },
                    { title: t("DataTime"), dataIndex: "CreateTime", align: "right" },
                    {
                        title: t("PGrid") + "(kW)", dataIndex: "PAC1Grid", align: "right", render: (v, r) => {
                            if (v == null) {
                                return "";
                            }
                            v = parseInt(v);
                            if (v > 0x7fffffff) {
                                v = v - 0x100000000;
                            }
                            return (v * 0.001).toFixed(3);
                        }
                    },
                    { title: t("VGrid") + "(V)", dataIndex: "VAC1Grid", align: "right", render: (v, r) => { return v != null ? (parseFloat(v) * 0.1).toFixed(1) : "" } },
                    { title: t("GridHz") + "(Hz)", dataIndex: "GridHz", align: "right", render: (v, r) => { return v != null ? (parseFloat(v) * 0.01).toFixed(2) : "" } },
                ]}
                dataSource={Combinations}
            />),
            onOk() {
            },
        });
    };

    const handleStatus = () => {
        Modal.info({
            maskClosable: true,
            width: "800px",
            icon: null,
            title: t("Inverter/Battery Alarms"),
            content: (<Table
                pagination={false}
                columns={[
                    { title: t("DataTime"), dataIndex: "CreateTime", align: "right" },
                    {
                        title: t("From"), dataIndex: "From", align: "left", render: (v, r) => {
                            return `[${v}] ${r.SN}`;
                        }
                    },
                    {
                        title: t("Name"), dataIndex: "Name", align: "left", render: (v, r) => {
                            return `${v}${r.From == "Battery" ? "(L" + r.Level.toString() + ")" : ""}`;
                        }
                    },
                ]}
                dataSource={faults}
            />),
            onOk() {
            },
        });
    };

    const handleForceSOCCalibration = () => {
        if (!Combinations || Combinations.length < 1){
            return;
        }

        let sns = [];
        Combinations.forEach((v) => {
            sns.push(v.SN);
        })

        refForceSOCCalibration.current.doForcingSOCCalibration();
    };

    const handleMeterDirectionDetection = () => {
        if (!Combinations || Combinations.length < 1){
            return;
        }

        let sns = [];
        Combinations.forEach((v) => {
            sns.push(v.SN);
        })

        refMeterDirectionDetection.current.doMeterDirectionDetection();
    };

    let InverterConnectionMode = 1
    let PAC1 = 0;
    let PAC2 = 0;
    let PAC3 = 0;
    let VAC1 = 0;
    let VAC2 = 0;
    let VAC3 = 0;
    let HZ1 = 0;
    let HZ2 = 0;
    let HZ3 = 0;
    let count = Combinations?.length || 0;
    let c1 = 0;
    let c2 = 0;
    let c3 = 0;
    let dataTime = null;
    let status = null; // 0:unknown, 1:online, 2:offline, 3:fault

    if (Combinations != null) {
        let faults1 = []; //for inverter
        let faults2 = []; //for battery
        Combinations.forEach((item, index) => {
            let mode = 1;
            if (item.InverterConnectionMode != null) {
                mode = item.InverterConnectionMode;
                if (InverterConnectionMode < mode) {
                    InverterConnectionMode = parseInt(mode);
                }
            }
            if (mode == 0 || mode == 1 || mode == 2) {
                if (item.PAC1Grid != null) {
                    let v = parseInt(item.PAC1Grid);
                    if (v > 0x7fffffff) {
                        v = v - 0x100000000;
                    }
                    PAC1 += v * 0.001;
                }
                if (item.VAC1Grid != null) {
                    VAC1 += parseInt(item.VAC1Grid) * 0.1;
                }
                if (item.GridHz != null) {
                    HZ1 = parseInt(item.GridHz) * 0.01;
                }
                c1++;
            } else if (mode == 3) {
                if (item.PAC1Grid != null) {
                    let v = parseInt(item.PAC1Grid);
                    if (v > 0x7fffffff) {
                        v = v - 0x100000000;
                    }
                    PAC2 += v * 0.001;
                }
                if (item.VAC1Grid != null) {
                    VAC2 += parseInt(item.VAC1Grid) * 0.1;
                }
                if (item.GridHz != null) {
                    HZ2 += parseInt(item.GridHz) * 0.01;
                }
                c2++;
            } else if (mode == 4) {
                if (item.PAC1Grid != null) {
                    let v = parseInt(item.PAC1Grid);
                    if (v > 0x7fffffff) {
                        v = v - 0x100000000;
                    }
                    PAC3 += v * 0.001;
                }
                if (item.VAC1Grid != null) {
                    VAC3 += parseInt(item.VAC1Grid) * 0.1;
                }
                if (item.GridHz != null) {
                    HZ3 += parseInt(item.GridHz) * 0.01;
                }
                c3++;
            }

            //
            if (item.CreateTime != null) {
                let dt = item.CreateTime;
                if (dt > dataTime || dataTime == null) {
                    dataTime = dt;
                }
            }
            if (status != 3) {
                if ((item.ErrorCode1 != null && item.ErrorCode1 != 0) || (item.ErrorCode2 != null && item.ErrorCode2 != 0)) {
                    status = 3;
                } else {
                    if (item.Batteries != null) {
                        for (let i = 0; i < item.Batteries.length; i++) {
                            if (item.Batteries[i].Alarms != null) {
                                for (let k in item.Batteries[i].Alarms) {
                                    if (item.Batteries[i].Alarms[k] != null && item.Batteries[i].Alarms[k] > 0) {
                                        status = 3;
                                        break;
                                    }
                                }
                            }
                            if (status == 3) {
                                break;
                            }
                        }
                    }
                    if (status != 3) {
                        if (item.IsOnline == 1) {
                            status = 1;
                        } else if (item.IsOnline == 0 && status == null) {
                            status = 2;
                        }
                    }
                }
            }

            //
            if (item.ErrorCode1 != null) {
                let ec = window.BigInt(item.ErrorCode1);
                for (let i = 0; i < 4; i++) {
                    let e = (ec >> window.BigInt(i * 16)) & 0xffffn;
                    if (e != 0 && e < 100) {
                        faults1.push({
                            From: "Inverter",
                            SN: item.SN,
                            Code: Number(e),
                            Name: t(InverterAlarmDefs.AlarmsIndexToName[e.toString()]),
                            CreateTime: item.CreateTime
                        });
                    }
                }
            }
            if (item.ErrorCode2 != null) {
                let ec = window.BigInt(item.ErrorCode2);
                for (let i = 0; i < 4; i++) {
                    let e = (ec >> window.BigInt(i * 16)) & 0xffffn;
                    if (e != 0 && e < 100) {
                        faults1.push({
                            From: "Inverter",
                            SN: item.SN,
                            Code: Number(e),
                            Name: t(InverterAlarmDefs.AlarmsIndexToName[e.toString()]),
                            CreateTime: item.CreateTime
                        });
                    }
                }
            }
            //
            let code = window.BigInt((item.BMSErrorCode1 ?? 0) + ((item.BMSErrorCode2 ?? 0) << 16));
            if (code != 0) {
                for (let i = 0n; i < 32n; i++) {
                    let e = (code >> i) & 1n;
                    let name = InverterAlarmDefs.AlarmsIndexToName[(i + 100n).toString()];
                    if (e != 0 && (name ?? "") != "") {
                        faults1.push({
                            From: "Inverter",
                            SN: item.SN,
                            Code: Number(i + 100n),
                            Name: t(name),
                            CreateTime: item.CreateTime
                        });
                    }
                }
            }
            faults1.sort((a, b) => a.Code - b.Code);

            //
            if (item.Batteries != null) {
                item.Batteries.forEach((item2, index2) => {
                    if (item2.Alarms) {
                        for (let k in item2.Alarms) {
                            if (BatteryAlarmDefs.MapAlarmNameToName2[k] && item2.Alarms[k] != null && item2.Alarms[k] > 0) {
                                faults2.push({
                                    From: "Battery",
                                    SN: item2.SN,
                                    Code: BatteryAlarmDefs.MapAlarmNameToCategorys[k],
                                    Name: t(BatteryAlarmDefs.MapAlarmNameToName2[k]),
                                    Level: item2.Alarms[k],
                                    CreateTime: item2.Alarms.CreateTime
                                });
                            }
                        }
                    }
                });
            }
            faults2.sort((a, b) => a.Code - b.Code);
        });
        if (Combinations.length > 0) {
            HZ1 = c1 ? HZ1 / c1 : HZ1;
            HZ2 = c2 ? HZ2 / c2 : HZ2;
            HZ3 = c3 ? HZ3 / c3 : HZ3;
            VAC1 = c1 ? VAC1 / c1 : VAC1;
            VAC2 = c2 ? VAC2 / c2 : VAC2;
            VAC3 = c3 ? VAC3 / c3 : VAC3;
        }
        if (dataTime != null) {
            dataTime = convertTimeZone(dataTime, "Asia/Shanghai", getBrowserTimezone());
        }

        faults = faults1.concat(faults2);
    }


    let xx = 84;
    let yy = height / 2 - 84 / 2;

    return (<svg xmlns="http://www.w3.org/2000/svg" x={x} y={y} width={width} height={height} fill="none" cursor={"pointer"}
        onClick={handleClick}
    >
        <SymbolAvgVoltage id="VoltageAvg" />
        <SymbolAvgHz id="HzAvg" />

        <svg x={0} y={30}>
            {status == 3 && <SvgButton y={0} width={160} height={60} bgColor="#ff5555" bgColor2="#cc0000" fontSize={16} title={t("System Alarms")} onClick={handleStatus} />}
            <svg y={70}>
                {status == 3 && <circle cursor={"pointer"} onClick={(e) => { e.stopPropagation(); handleStatus(); }} cx={10} cy={10} r={9} fill="#ff0000" />}
                {status == 2 && <circle cursor={"default"} onClick={(e) => e.stopPropagation()} cx={10} cy={10} r={9} fill="#ccc" />}
                {status == 1 && <circle cursor={"default"} onClick={(e) => e.stopPropagation()} cx={10} cy={10} r={9} fill="#00ff00" />}
                {status == null && <circle cursor={"default"} onClick={(e) => e.stopPropagation()} cx={10} cy={10} r={9} fill="#ccc" />}
                <text x={23} y={0} fontSize={18} fill='black' alignmentBaseline='hanging' >{dataTime ?? "YYYY-MM-DD HH:mm:ss"}</text>
                <svg onClick={(e) => {
                    e.stopPropagation(); onRefresh && onRefresh();
                }} className="reload" x={210} y={0} width={18} height={18} viewBox="64 64 896 896" focusable="false" data-icon="reload" fill='#1d80b5' aria-hidden="true">
                    <rect width={896} height={896} rx={48} ry={48} fill="#fff" stroke='none'></rect>
                    <path d="M909.1 209.3l-56.4 44.1C775.8 155.1 656.2 92 521.9 92 290 92 102.3 279.5 102 511.5 101.7 743.7 289.8 932 521.9 932c181.3 0 335.8-115 394.6-276.1 1.5-4.2-.7-8.9-4.9-10.3l-56.7-19.5a8 8 0 00-10.1 4.8c-1.8 5-3.8 10-5.9 14.9-17.3 41-42.1 77.8-73.7 109.4A344.77 344.77 0 01655.9 829c-42.3 17.9-87.4 27-133.8 27-46.5 0-91.5-9.1-133.8-27A341.5 341.5 0 01279 755.2a342.16 342.16 0 01-73.7-109.4c-17.9-42.4-27-87.4-27-133.9s9.1-91.5 27-133.9c17.3-41 42.1-77.8 73.7-109.4 31.6-31.6 68.4-56.4 109.3-73.8 42.3-17.9 87.4-27 133.8-27 46.5 0 91.5 9.1 133.8 27a341.5 341.5 0 01109.3 73.8c9.9 9.9 19.2 20.4 27.8 31.4l-60.2 47a8 8 0 003 14.1l175.6 43c5 1.2 9.9-2.6 9.9-7.7l.8-180.9c-.1-6.6-7.8-10.3-13-6.2z"></path>
                </svg>
            </svg>
        </svg>

        <SvgGrid SN={SN} x={0} y={yy} width={84} height={84} />

        {InverterConnectionMode == 0 || InverterConnectionMode == 1 ?
            <svg x={xx} y={yy} >
                <text x={130} y={25} fontSize="20" fill="black" textAnchor="end">
                    {PAC1.toFixed(3)} kW
                    {count > 1 && <tspan dy="-1.2em" className='sigma'>(Σ{c1})</tspan>}
                </text>
                <text x={150 + 56} y={25} fontSize="20" fill="black" textAnchor="end">{VAC1.toFixed(1)} {count <= 1 && "V"}</text>
                {count > 1 && <use href="#VoltageAvg" x={150 + 56} y={5} fontSize="20" width="24" height="20" />}
                <text x={150 + 80 + 60} y={25} fontSize="20" fill="black" textAnchor="end">{HZ1.toFixed(2)} {count <= 1 && "Hz"}</text>
                {count > 1 && <use href="#HzAvg" x={150 + 80 + 60} y={5} fontSize="20" width="40" height="20" />}
            </svg>
            :
            <svg x={xx} y={yy - 6} >
                <text x={130} y={30} fontSize="20" fill="black" textAnchor="end">
                    {PAC1.toFixed(3)} kW
                    {count > 1 && <tspan dy="-1.2em" className='sigma'>(Σ{c1})</tspan>}
                </text>
                <text x={150 + 56} y={30} fontSize="20" fill="black" textAnchor="end">{VAC1.toFixed(1)} {count <= 1 && "V"}</text>
                {count > 1 && <use href="#VoltageAvg" x={150 + 56} y={10} fontSize="20" width="24" height="20" />}
                <text x={150 + 80 + 60} y={30} fontSize="20" fill="black" textAnchor="end">{HZ1.toFixed(2)} {count <= 1 && "Hz"}</text>
                {count > 1 && <use href="#HzAvg" x={150 + 80 + 60} y={10} fontSize="20" width="40" height="20" />}

                <text x={130} y={60} fontSize="20" fill="black" textAnchor="end">
                    {PAC2.toFixed(3)} kW
                    {count > 1 && <tspan dy="-1.2em" className='sigma'>(Σ{c2})</tspan>}
                </text>
                <text x={150 + 56} y={60} fontSize="20" fill="black" textAnchor="end">{VAC2.toFixed(1)} {count <= 1 && "V"}</text>
                {count > 1 && <use href="#VoltageAvg" x={150 + 56} y={40} fontSize="20" width="24" height="20" />}
                <text x={150 + 80 + 60} y={60} fontSize="20" fill="black" textAnchor="end">{HZ2.toFixed(2)} {count <= 1 && "Hz"}</text>
                {count > 1 && <use href="#HzAvg" x={150 + 80 + 60} y={40} fontSize="20" width="40" height="20" />}

                <text x={130} y={90} fontSize="20" fill="black" textAnchor="end">
                    {PAC3.toFixed(3)} kW
                    {count > 1 && <tspan dy="-1.2em" className='sigma'>(Σ{c3})</tspan>}
                </text>
                <text x={150 + 56} y={90} fontSize="20" fill="black" textAnchor="end">{VAC3.toFixed(1)} {count <= 1 && "V"}</text>
                {count > 1 && <use href="#VoltageAvg" x={150 + 56} y={70} fontSize="20" width="24" height="20" />}
                <text x={150 + 80 + 60} y={90} fontSize="20" fill="black" textAnchor="end">{HZ3.toFixed(2)} {count <= 1 && "Hz"}</text>
                {count > 1 && <use href="#HzAvg" x={150 + 80 + 60} y={70} fontSize="20" width="40" height="20" />}
            </svg>
        }

        <SvgButton y={yy+84+15} width={i18n.language=="en" ? 200 : 140} height="50" bgColor="#1d80b5" fontSize={14} title={t("Force SOC Calibration")} onClick={handleForceSOCCalibration} />
        <SvgButton x={(i18n.language=="en" ? 200 : 140)}  y={yy+84+15} width={i18n.language=="en" ? 210 : 140} height="50" bgColor="#1d80b5" fontSize={14} title={t("Meter Direction Detection")} onClick={handleMeterDirectionDetection} />

        <ForceSOCCalibration ref={refForceSOCCalibration} sns={sns} />
        <MeterDirectionDetection ref={refMeterDirectionDetection} sns={sns} />
    </svg>);
}

export default SvgRight;